import React from "react";
import ReactDom from "react-dom";
import { AppContainer } from "react-hot-loader";
import Api from "../service/Api";
import Analytics from "../service/Analytics";
import BindDataToStore from "../utils/BindDataToStore";
import "../../less/global.less";

const widgets = require.context("../widgets", true, /\.jsx?$/);
const autowire = require.context("../components/autowire", true, /\.jsx?$/);
const stores = require.context("../stores", true, /\.js$/);

window.tb = {
  widgets: (window.tb && window.tb.widgets) || {},
  stores: (window.tb && window.tb.stores) || {},

  /**
   * causes all widgets registered on the page to refresh with new code
   */
  refresh() {
    Object.keys(this.widgets).forEach(id => {
      this.createWidget(id, this.widgets[id].widget, this.widgets[id].data);
    });
  },

  /**
   *
   * @param {string} id
   * @param {string} storeFile
   * @param {object} data Data whose properties that can be merged directly to the matching properties of the store.
   * @param {object} constructorData Data passed to the store class constructor method.
   */
  createStore(id, storeFile, data = {}) {
    const StoreClass = stores(`./${storeFile}.js`).default;
    // pass constructor arguments to the Store class (if any)
    // Ideally, instead of passing an object, we should pass each object property to the constructor separately, like
    // (data.prop1, data.prop2). Although this can be achieved with property spreading (...constructorData) it
    // won't work when Prototype.js is loaded in the same html page this createStore() method is called.
    const newStore = (this.stores[id] = new StoreClass(data));

    return newStore;
  },

  loadStore(id, storeType, url, initData) {
    const store = this.createStore(id, storeType, {
      loading: true,
      ...initData,
    });

    Api.get(url)
      .then(data => {
        BindDataToStore(store, data, {
          loading: false,
        });
      })
      .catch(error => {
        store.loading = false;
        store.error = error;
      });

    return store;
  },

  // Similar to loadStore but instead of using extendObservable to bind the async data we'll use
  // a setter method to pass the data.
  loadStoreAsync(id, storeType, url, initData) {
    const store = this.createStore(id, storeType, {
      loading: true,
      ...initData,
    });

    Api.get(url)
      .then(data => {
        Object.assign(data, { loading: false });
        store.init(data);
      })
      .catch(error => {
        store.loading = false;
        store.error = error;
      });

    return store;
  },

  /**
   *
   * @param {string} id An id selector
   * @param {string} widget A widget name
   * @param {object} data A key/value JS object
   * @param {boolean} destroy if true, will replace all existing state and create fresh
   * @return {ReactComponent} The created react instance
   */
  createWidget(id, widget, data, destroy = false) {
    this.widgets[id] = {
      id,
      widget,
      data,
    };

    const Component = widgets(`./${widget}.jsx`).default;

    const container = document.getElementById(id);

    if (destroy) {
      ReactDom.unmountComponentAtNode(container);
    }

    // AppContainer allows for reloading modules without losing internal state
    return ReactDom.render(
      <AppContainer>
        <Component {...data} />
      </AppContainer>,
      container,
    );
  },

  /**
   *
   * @param {string} querySelector A css query selector.
   * @param {string} widget A widget name
   * @return {ReactComponent} The created react instance
   */
  autowireWidget(querySelector, widget) {
    const Component = autowire(`./${widget}.jsx`).default;
    const elements = document.querySelectorAll(querySelector);

    elements.forEach(element => {
      // Get all data-* attributes in the element in order to pass as props.
      // value is an array of node attributes.
      const attributes = [].filter.call(element.attributes, at => /^data-/.test(at.name));

      const data = {};

      attributes.forEach(attribute => {
        // remove the 'data-' prefix in order to get the property name.
        const property = attribute.name.replace("data-", "");
        // cast empty value to true.
        data[property] = attribute.value === "" || attribute.value === null ? true : attribute.value;
      });

      const id = element.getAttribute("id");

      if (!id) {
        throw new Error("An id was expected for the autowire element");
      }

      this.widgets[id] = {
        id,
        widget,
        data,
      };

      // AppContainer allows for reloading modules without losing internal state
      ReactDom.render(
        <AppContainer>
          <Component {...data} />
        </AppContainer>,
        element,
      );
    });
  },

  /**
   * Attaches additional metadata to all events fired on this page
   *
   * @param key The field to store the value
   */
  analyticsAttach(key, value) {
    Analytics.metadata[key] = value;
  },
};

if (module.hot) {
  module.hot.accept();

  // when using webpack, this entire global.js script is executed again so we refresh any widgets written to page with
  // old code. AppContainer allows for reloading modules without losing internal state
  window.tb.refresh();
}
